<template>
    <div
        v-if="renderToInitialHtml || renderedAtLeastOnce"
        v-show="isModalShown"
        style="position: absolute; z-index: 1150"
        :data-modal-name="modalName"
        @mousedown="handleCloseModal"
    >
        <div ref="modalDialog" role="dialog" :class="`modal fade d-block ${modalClass}`" data-cy="modal-content">
            <div :class="`modal-dialog modal-${size} modal-dialog-centered`">
                <div ref="modalContent" class="modal-content">
                    <header v-if="showHeader" class="modal-header">
                        <h5 class="modal-title" v-html="title"></h5>

                        <button
                            v-if="showCloseButton && shouldShowCloseButton"
                            ref="closeButton"
                            type="button"
                            aria-label="Close"
                            class="close"
                            data-cy="close"
                            @click="closeModal"
                        >
                            &times;
                        </button>
                    </header>

                    <div class="modal-body">
                        <slot :params="params" />
                    </div>
                </div>
            </div>
        </div>
        <div class="modal-backdrop"></div>
    </div>
</template>

<script lang="ts">
import { useNuxtApp } from 'nuxt/app'

export default defineNuxtComponent({
    inheritAttrs: false,

    props: {
        modalClass: {
            type: String,
            default: '',
        },

        modalName: {
            type: String,
            default: '',
        },

        title: {
            type: String,
            default: '',
        },

        size: {
            type: String,
            default: 'md',
        },

        backdropClose: {
            type: Boolean,
            default: true,
        },

        showCloseButton: {
            type: Boolean,
            default: true,
        },

        renderToInitialHtml: {
            type: Boolean,
            default: false,
        },

        showHeader: {
            type: Boolean,
            default: true,
        },
    },

    data() {
        return {
            params: null,
            isModalShown: false,
            renderedAtLeastOnce: false,
            shouldShowCloseButton: true,
            scrollY: 0,
        }
    },

    mounted() {
        const { $el } = this

        $el.remove()
        document.body.appendChild($el)

        useNuxtApp().$bus.$on('showModal', (modalName: string) => {
            this.isModalShown = this.modalName === modalName
            if (this.isModalShown) {
                this.isModalShownChanged()
            }
        })

        useNuxtApp().$bus.$on('hideModal', () => {
            this.hideModal()
        })

        useNuxtApp().$bus.$on('hideModalImmediately', () => {
            this.hideModalImmediately()
        })
    },

    methods: {
        handleCloseModal(event: Event) {
            // @ts-ignore
            if (!(event.path || event?.composedPath?.()).includes(this.$refs?.modalContent) && this.backdropClose) {
                this.closeModal()
            }
        },

        closeModal() {
            this.shouldShowCloseButton = false
            setTimeout(() => {
                this.$emit('close')
                this.hideModal()
            }, 0)
        },

        hideModal() {
            this.shouldShowCloseButton = false
            setTimeout(() => {
                this.$refs.modalDialog?.classList.remove('show')
                document.body.classList.remove('modal-open')
                document.body.style.top = ''
                window.scrollTo(0, this.scrollY)

                setTimeout(() => {
                    this.isModalShown = false
                    this.isModalShownChanged()
                    useNuxtApp().$bus.$emit('hidden', this.modalName)
                }, 200)
            }, 100)
        },

        hideModalImmediately() {
            this.shouldShowCloseButton = false
            this.$refs.modalDialog?.classList.remove('show')
            document.body.classList.remove('modal-open')
            document.body.style.top = ''
            window.scrollTo(0, this.scrollY)

            this.isModalShown = false
            this.isModalShownChanged()
            useNuxtApp().$bus.$emit('hidden', this.modalName)
        },

        logKey(e: KeyboardEvent) {
            if (e.keyCode === 27) {
                this.hideModal()
            }
        },
        isModalShownChanged() {
            const isShown = this.isModalShown
            if (isShown) {
                this.scrollY = window.scrollY

                this.renderedAtLeastOnce = true
                this.shouldShowCloseButton = true
                document.body.classList.add('modal-open')
                document.body.style.top = `-${this.scrollY}px`

                setTimeout(() => {
                    this.$refs.modalDialog?.classList.add('show')
                    this.$emit('shown')

                    setTimeout(() => {
                        ;(this.$refs.closeButton as HTMLElement)?.focus()
                    }, 500)
                }, 0)

                document.addEventListener('keyup', this.logKey)

                if (window) {
                    this.$nextTick(function () {
                        const nuxtApp = useNuxtApp()
                        const lazyload = nuxtApp.$lazyload
                        const media = document.querySelectorAll('.lazyload') as unknown as HTMLElement[]
                        media.forEach((m) => lazyload(m))
                    })
                }
            } else {
                //  document.body.classList.remove('modal-open')
            }
        },
    },
})
</script>

<style lang="scss" scoped>
@import '../node_modules/bootstrap/scss/functions';
@import '../node_modules/bootstrap/scss/variables';
@import '../node_modules/bootstrap/scss/mixins';

.modal {
    .modal-content {
        font-size: 14px;
        box-shadow: 0 0.5rem 1rem rgb(0 0 0 / 20%);
        border: none;
        padding: 0 1rem 1rem 1rem;
        border-radius: 0;
        height: 100vh;

        @include screen-md {
            border-radius: 10px;
            height: auto;
        }
    }

    .modal-header {
        border-bottom: 0;
        padding: 0;
        margin-bottom: 1rem;
        font-weight: 500;
        justify-content: space-between;

        .modal-title {
            padding-top: 1rem;
            font-size: 18px;
        }

        .close {
            font-size: 0;
            width: 45px;
            height: 45px;
            position: relative;
            background-color: transparent;
            border: none;
            margin-right: -1rem;
            cursor: pointer;

            &::after {
                content: '';
                left: 0;
                top: 0;
                width: 45px;
                height: 45px;
                position: absolute;
                background: url('/nuxt-assets/icons/close.svg') no-repeat 50%;
            }
        }
    }

    .modal-body {
        padding: 0;
    }

    @media (max-width: 768px) {
        .modal-body {
            max-width: 100%;
            overflow-x: hidden;
            height: 90vh;
            overflow-y: auto;
            padding: 15px 0 10px 0;
        }
    }
}

.modal-full {
    width: 100vw;
    height: 100vh;
    background-color: #fff;
    padding-left: 0 !important;

    .modal-dialog {
        max-width: 100%;
        margin: 0;
    }

    .modal-content {
        border: none;
        border-radius: 0;
    }

    .modal-header {
        position: relative;
        border: none;

        .close {
            position: absolute;
            top: 2rem;
            right: 2rem;
            z-index: 12;
            font-size: 0;

            &::after {
                content: '';
                display: inline-block;
                width: 20px;
                height: 20px;
                background: url(https://www.pilulka.cz/dist/a0862.svg) no-repeat;
                background-size: 19px 19px;
            }
        }
    }

    .modal-body {
        padding: 0;
    }
}

.modal-max {
    .carousel-control-prev-icon,
    .carousel-control-next-icon {
        width: 65px;
        height: 65px;
        position: absolute;
    }

    .carousel-control-prev,
    .carousel-control-next {
        font-size: 0;
        width: 50px;
        opacity: 1;
    }

    .carousel-control-next {
        right: 0;
    }

    .carousel-inner,
    .carousel-item {
        height: 100% !important;
    }

    .carousel {
        height: auto;
        max-height: 100%;
    }

    .modal-dialog {
        height: calc(100%);

        @include media-breakpoint-up(sm) {
            max-width: calc(100vw - 80px);
            height: calc(100% - 3.5rem);
        }
    }

    .modal-content {
        box-shadow: 0 0.5rem 1rem rgb(0 0 0 / 20%);
        border: none;
        max-height: 100%;
        height: 100%;
    }

    .modal-body {
        flex-direction: column;
        display: flex;
        justify-content: center;
        height: calc(100% - 45px);
    }

    @include media-breakpoint-up(md) {
        .modal-xl {
            align-items: stretch;
        }
    }

    .modal-image {
        padding: 0 1rem 30px 1rem;
        height: calc(100% - 45px);

        .carousel-control-prev-icon,
        .carousel-control-next-icon {
            width: 50px;
            height: 50px;
        }

        .carousel {
            width: 100%;
        }

        img {
            max-height: 100%;
        }
    }

    .modal-caption {
        text-align: center;
        color: $color-grey-extra-dark;
        font-size: 1rem;
        margin: 0.5em 0;
        font-weight: 500;
    }
}

.modal-backdrop {
    opacity: 1;
    background: hsla(0, 0%, 72%, 0.7);
}

.modal-max .modal-content {
    height: 100%;
}

.modal-90 {
    width: 100vw;
    max-width: 100vw;
    height: 100vh;
    background-color: #fff;
    padding-left: 0 !important;

    @media only screen and (max-width: ($screen-md - 1px)) {
        margin: 0;
    }

    @include screen-md {
        width: 90vw;
        max-width: 90vw;
        height: 94vh;
        margin: 3vh 5vw;
        min-height: auto;
    }

    .modal-content {
        border: none;
        border-radius: 0;
        height: 100%;
        padding: 0;
    }

    .modal-body {
        padding: 0;
    }
}

.modal-rounded {
    .modal-content {
        border-radius: $map-border-radius;
    }
    .modal-dialog {
        border-radius: $map-border-radius;
    }
}
</style>
